import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import { logger } from 'packages/logger';
import { loadScript } from 'tradera/utils/script';
import Analytics from 'packages/analytics';
import {
    useCurrentLanguage,
    useTranslator,
    useUrlLocalizer
} from 'tradera/lang/translate';
import { getLoginLocaleFromLanguage } from './get-login-locale-from-language';
import * as loginAnalytics from 'tradera/legacy/static/app/ui/layout/login-signup-analytics';
import { KlarnaButton } from 'tradera/components/social-buttons/social-buttons';

const KlarnaLoginButton = (props) => {
    const {
        klarnaAppId,
        klarnaEnvironment,
        isModal,
        className,
        compact,
        disabled,
        onError,
        tabIndex
    } = props;
    const { toLocalizedUrl } = useUrlLocalizer();
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslator();
    const { language } = useCurrentLanguage();
    const locale = getLoginLocaleFromLanguage(language);

    const redirectUri = new URL('login', window.location.origin).toString();

    // Pre-loading & init needed on load to prevent popup blocking in Safari
    useEffect(() => {
        (async () => {
            window.onKlarnaSignIn = async (tokens) => {
                try {
                    const { refresh_token, id_token } = tokens;

                    const verifyResponse = await axios.post(
                        '/login/klarna/verify' + window.location.search,
                        {
                            accessToken: id_token,
                            refreshToken: refresh_token,
                            UserId: 'NOT_USED'
                        }
                    );

                    if (isModal) {
                        Analytics.trackEvent({
                            category: 'Top navigation',
                            action: 'Login menu',
                            label: 'Button - Klarna login'
                        });
                    }
                    loginAnalytics.flow.set(loginAnalytics.FLOW.LOGIN);
                    loginAnalytics.method.set(loginAnalytics.METHOD.KLARNA);
                    loginAnalytics.trackEvent('user-login-submit');

                    window.top.location = verifyResponse.data.returnUrl;
                } catch (error) {
                    const message =
                        error?.response?.data?.error ||
                        error?.message ||
                        error?.error;
                    switch (message) {
                        case 'popup_closed_by_user':
                            break;
                        case 'MemberAuth_IncorrectUserOrPassword':
                            window.top.location = toLocalizedUrl(
                                '/login?connect=Klarna'
                            );
                            break;
                        default:
                            onError(error);
                            logger(message);
                            break;
                    }
                }
            };

            window.onKlarnaSignInError = () => {
                loginAnalytics.flow.set(loginAnalytics.FLOW.LOGIN);
                loginAnalytics.method.set(loginAnalytics.METHOD.KLARNA);
                loginAnalytics.trackEvent('user-login-submit-failed');
            };

            await loadScript(
                `https://x.klarnacdn.net/sign-in-with-klarna/v1/lib.js`,
                {
                    dataAttributes: {
                        'client-id': klarnaAppId,
                        market: 'SE',
                        scope: 'offline_access profile email billing_address phone national_identification_number create_payment_session',
                        environment: klarnaEnvironment,
                        'on-sign-in': 'onKlarnaSignIn',
                        'on-error': 'onKlarnaSignInError',
                        locale: locale || 'sv-SE',
                        'hide-overlay': true,
                        'redirect-uri': redirectUri
                    }
                }
            );
            const Klarna = window.Klarna;

            if (!Klarna) {
                logger('Klarna SDK could not be loaded by Klarna login button');
                return;
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [klarnaAppId, locale]);

    const performLogin = async () => {
        const Klarna = window.Klarna;
        await Klarna.SignIn.onButtonClick();
        return;
    };

    const onClick = async () => {
        if (disabled) {
            return;
        }

        setIsLoading(true);
        onError(null);
        try {
            await performLogin();
        } catch (error) {
            onError(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        //<klarna-sign-in></klarna-sign-in>
        <KlarnaButton
            onClick={onClick}
            disabled={disabled}
            tabIndex={tabIndex}
            loading={isLoading}
            className={className}
            round={compact}>
            {!compact && t('login_withProvider', { provider: 'Klarna' })}
        </KlarnaButton>
    );
};

KlarnaLoginButton.propTypes = {
    className: PropTypes.string,
    isModal: PropTypes.string,
    onError: PropTypes.func,
    disabled: PropTypes.bool,
    compact: PropTypes.bool,
    tabIndex: PropTypes.number
};

KlarnaLoginButton.defaultProps = {
    className: '',
    tabIndex: 0
};

export default KlarnaLoginButton;
